import { AxiosError } from 'axios'
import { useCallback, useContext, useEffect, useMemo, useState, Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { ErrorsContext } from '../contexts/errors_context'
import { usersRepository } from '../repositories/users_repository'
import { Blog, Feedback, Filters, User } from '../types'
import { GenericNotFound } from './generic_not_found'
import { UserBanner } from './user_banner'

import '../stylesheets/User.scss'
import clsx from 'clsx'
import { Units } from './units'
import { feedbacksRepository } from '../repositories/feedbacks_repository'
import { FeedbackView } from './feedback'
import { SearchContext } from '../contexts/search_context'
import { SelectedFilters } from './selected_filters'
import { SortSelect } from './sort_select'
import { hrefWithSearch, readBrowseFilters, searchForSort, sortsForPath, withSortFrom } from '../lib/browse_params'
import { applyDocumentMeta, collectionPageMeta } from '../lib/seo_meta'
import { BlogsIndex } from './blog_index'
import { AuthContext } from '../contexts/auth_context'

type FeedbacksProps = Readonly<{
  userId: number
  currentPage: number
}>

const Feedbacks = ({ userId, currentPage }: FeedbacksProps) => {
  const { t } = useTranslation()
  const { addError } = useContext(ErrorsContext)

  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [feedbacksLoading, setFeedbacksLoading] = useState<boolean>(false)

  const getFeedbacks = useCallback((uid: number, page: number) => {
    setFeedbacksLoading(true)

    feedbacksRepository
      .index({ userId: uid, params: { page } })
      .then(({ feedbacks }) => {
        setFeedbacks(feedbacks)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setFeedbacksLoading(false)
      })
  }, [addError])

  useEffect(() => {
    getFeedbacks(userId, currentPage)
  }, [currentPage, getFeedbacks, userId])

  return <>
    <h1>{ t('profile.ratings') }</h1>
    {
      feedbacksLoading
        ? t('app.loading')
        : feedbacks.map((feedback) => <FeedbackView feedback={ feedback } key={ feedback.id } />)
    }
  </>
}

type UserProfileProps = Readonly<{
  screen: string
}>

const UserProfile = ({ screen }: UserProfileProps) => {
  const { addError } = useContext(ErrorsContext)
  const { endpointForExistingFilters, searchString, setSearchboxPlaceholder, setIsUserProfile, filters, hasFilters, setFilters, defaultFilters, filterMenuDisplayed, setFilterMenuDisplayed } = useContext(SearchContext)

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const sort = useMemo(() => readBrowseFilters(location.search, location.pathname).sort, [location.pathname, location.search])

  const BlogCreate = lazy(() => import('./blog_create').then(module => ({ default: module.BlogCreate })))
  const BlogView = lazy(() => import('./blog_view').then(module => ({ default: module.BlogView })))

  const [profileUser, setProfileUser] = useState<User>()
  const [profileUserLoading, setProfileUserLoading] = useState<boolean>(true)

  const path = useParams()

  const pathUserId = useMemo(() => {
    const intId = parseInt(path.id ?? '', 10)

    if (!isNaN(intId) && intId > 0) return String(intId)

    if (path.id === 'me') return 'me'

    return undefined
  }, [path.id])

  const [profileUserId, setProfileUserId] = useState<string|undefined>()

  const setSort = useCallback((nextSort: string) => {
    navigate(hrefWithSearch(location.pathname, searchForSort(location.search, location.pathname, nextSort)))
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const intId = parseInt(path.id ?? '')

    if (!isNaN(intId) && intId > 0) {
      setProfileUserId(String(intId))
      return
    }

    if (path.id === 'me') {
      setProfileUserId('me')
    }
  }, [path])

  useEffect(() => {
    setProfileUser(undefined)
  }, [profileUserId])

  const unitId = useMemo<number|undefined>(() => {
    const intId = parseInt(path.item_id ?? '')

    if (!isNaN(intId) && intId > 0) {
      return intId
    }
  }, [path])

  // /u/me still waits for the numeric id. A numeric unit URL can paint the
  // unit while the seller profile request is in flight.
  const showingUnit = unitId != null && !!pathUserId && pathUserId !== 'me'

  const blogId = useMemo<number|undefined>(() => {
    const intId = parseInt(path.blog_id ?? '')

    if (!isNaN(intId) && intId > 0) {
      return intId
    }
  }, [path])

  const getUser = useCallback((userId: string) => {
    if (userId === 'me') {
      usersRepository
        .getSelf()
        .then(({ user }) => {
          setProfileUserId(String(user.id))
          setProfileUser(user)
        })
        .catch((err: AxiosError) => {
          setProfileUserId(undefined)
          addError?.(err)
        })
        .finally(() => {
          setProfileUserLoading(false)
        })
    } else {
      usersRepository
        .getUser(Number(userId))
        .then(({ user }) => {
          setProfileUser(user)
        })
        .catch((err: AxiosError) => {
          setProfileUserId(undefined)
          addError?.(err)
        })
        .finally(() => {
          setProfileUserLoading(false)
        })
    }
  }, [addError])

  useEffect(() => {
    if (!profileUserId) return

    if (!profileUser) {
      getUser(profileUserId)
    }
  }, [getUser, profileUserId, profileUser])

  const collectionName = useMemo(() => {
    if (!profileUser?.displayName) return t('cards.item.default_display_name')

    return profileUser.displayName
  }, [t, profileUser])

  const blogCreateCallback = useCallback((blog: Blog) => {
    if (!blog.id) return
    if (!profileUserId) return

    navigate(`/u/${profileUserId}/blog/${blog.id}`)
  }, [navigate, profileUserId])

  const { isAuthenticated, currentUser } = useContext(AuthContext)

  const isMine = useMemo(() => {
    if (!isAuthenticated || !profileUserId) return false

    if (profileUserId === 'me') return true

    const intId = parseInt(profileUserId)

    return intId === currentUser?.id
  }, [isAuthenticated, profileUserId, currentUser?.id])

  const organiseModeAvailable = useMemo(() => {
    if (!isMine) return false

    // compare filters to default filters using stringified JSON
    if (JSON.stringify(filters) === JSON.stringify(defaultFilters)) {
      // must be sorting by ordinality_asc
      return sort === 'ordinality_asc'
    }

    return false
  }, [defaultFilters, filters, isMine, sort])

  const [organiseMode, setOrganiseMode] = useState(false)
  const [photoEdit, setPhotoEdit] = useState(false)

  const toggleOrganiseMode = useCallback(() => {
    setPhotoEdit(false)
    setOrganiseMode(current => !current)
  }, [])

  const togglePhotoEdit = useCallback(() => {
    setOrganiseMode(false)
    setPhotoEdit(current => !current)
  }, [])

  useEffect(() => {
    if (!organiseModeAvailable) {
      setOrganiseMode(false)
    }
  }, [organiseModeAvailable])

  const currentView = useMemo(() => {
    const activeUserId = profileUserId && profileUserId !== 'me'
      ? profileUserId
      : (pathUserId && pathUserId !== 'me' ? pathUserId : undefined)

    if (screen === 'cards' && unitId && activeUserId) {
      return <Units userId={ activeUserId } user={ profileUser } unitId={ unitId } sort={ sort } organising={ organiseMode } setOrganising={ setOrganiseMode } photoEdit={ isMine && photoEdit } />
    }

    if (!activeUserId) {
      return
    }

    if (screen === 'reviews') {
      return <div className='narrow'>
        <Feedbacks userId={ Number(profileUserId) } currentPage={ 0 } />
      </div>
    }

    if (screen === 'blog-create') {
      return <div className='narrow'>
        <Suspense fallback={<div>{ t('app.loading') }</div>}>
          <BlogCreate callback={ blogCreateCallback } />
        </Suspense>
      </div>
    }

    if (screen === 'blog' || screen === 'blog-edit') {
      if (blogId) {
        return <div className={ clsx({ narrow: !!(screen === 'blog-edit'), 'semi-narrow': !(screen === 'blog-edit') }) }>
          <Suspense fallback={<div>{ t('app.loading') }</div>}>
            <BlogView id={ Number(blogId) } userId={ Number(profileUserId) } editMode={ !!(screen === 'blog-edit') } />
          </Suspense>
        </div>
      }

      return <BlogsIndex userId={ Number(profileUserId) } canEdit={ currentUser?.id === Number(profileUserId) } />
    }

    return <Units userId={ activeUserId } user={ profileUser } unitId={ unitId } sort={ sort } organising={ organiseMode } setOrganising={ setOrganiseMode } photoEdit={ isMine && photoEdit } />
  }, [blogCreateCallback, blogId, currentUser?.id, isMine, organiseMode, pathUserId, photoEdit, screen, sort, profileUser, profileUserId, t, unitId])

  const allCardsEndpoint = useMemo(() => {
    if (!endpointForExistingFilters) return ''

    const f: Filters = {
      ...filters,
      selectedStocks: [],
      priceRange: {
        ...filters.priceRange,
        min: undefined,
      }
    }

    const search = withSortFrom(endpointForExistingFilters(f, searchString) ?? '', location.search)
    return search ? `?${search}` : ''
  }, [endpointForExistingFilters, filters, location.search, searchString])

  const forSaleCardsEndpoint = useMemo(() => {
    if (!endpointForExistingFilters) return ''

    const f: Filters = {
      ...filters,
      selectedStocks: ['for_sale'],
      priceRange: {
        ...filters.priceRange,
        min: 1,
      }
    }

    const search = withSortFrom(endpointForExistingFilters(f, searchString) ?? '', location.search)
    return search ? `?${search}` : ''
  }, [endpointForExistingFilters, filters, location.search, searchString])

  const sortMenu = useMemo(() => {
    if (screen !== 'cards') return null

    return <SortSelect sorts={ sortsForPath(location.pathname) } value={ sort } onChange={ setSort } disabled={ organiseMode } />
  }, [location.pathname, organiseMode, screen, setSort, sort])

  const clearFilterCallback = useCallback((f: Filters, newSearchString?: string) => {
    if (!endpointForExistingFilters || !profileUserId || !setFilters) return

    const search = withSortFrom(endpointForExistingFilters(f, newSearchString ?? searchString) ?? '', location.search)
    navigate(hrefWithSearch(`/u/${profileUserId}`, search))

    setFilters(f)
  }, [endpointForExistingFilters, location.search, navigate, searchString, setFilters, profileUserId])

  const selectedFiltersUi = useMemo(() => {
    if (!hasFilters) return null

    return <SelectedFilters filters={ filters } searchString={ searchString } hide={ ['stocks'] } callback={ clearFilterCallback } />
  }, [clearFilterCallback, filters, hasFilters, searchString])

  const organiseButtons = useMemo(() => {
    if (!isMine || organiseMode) return null

    return <div className='organise-buttons'>
      <button className='tool' onClick={ togglePhotoEdit }>
        { photoEdit ? t('collections.view.button_edit_cards_close') : t('collections.view.button_edit_cards') }
      </button>
      {
        photoEdit
          ? null
          : <button className='tool' onClick={ toggleOrganiseMode } disabled={ !organiseModeAvailable }>
            { t('collections.view.layout_organise.button_start') }
          </button>
      }
    </div>
  }, [isMine, organiseMode, organiseModeAvailable, photoEdit, t, toggleOrganiseMode, togglePhotoEdit])

  const headerFilters = useMemo(() => {
    if (screen !== 'cards') return null

    return <>
      { selectedFiltersUi }
      <div className='toolbar'>
        { sortMenu }
        <button type='button' className='tool open-filters' aria-expanded={ filterMenuDisplayed } aria-controls='browse-filter-sheet' onClick={ () => setFilterMenuDisplayed?.(!filterMenuDisplayed) }>{ t('search.filters') }</button>
        { organiseButtons }
      </div>
    </>
  }, [filterMenuDisplayed, organiseButtons, screen, selectedFiltersUi, setFilterMenuDisplayed, sortMenu, t])

  const header = useMemo(() => {
    if (unitId ?? !profileUserId) return null

    return <>
      <div className='user-header'>
        <UserBanner clickable={ screen !== 'cards' } userId={ String(profileUserId) } displayName={ collectionName } salesLevel={ profileUser?.salesLevel } salesRating={ profileUser?.salesRating } />
        <div className='tabs'>
          <Link to={ `/u/${profileUserId}${allCardsEndpoint}` } className={ clsx({ selected: screen === 'cards' && !filters.priceRange.min }) }>{ t('profile.all') }</Link>
          <Link to={ `/u/${profileUserId}${forSaleCardsEndpoint}` } className={ clsx({ selected: screen === 'cards' && filters.priceRange.min }) }>{ t('profile.for_sale') }</Link>
          <Link to={ `/u/${profileUserId}/blog` } className={ clsx({ selected: ['blog', 'blog-create'].find(s => s === screen) }) }>{ t('profile.blog') }</Link>
          { /* { followButton } */ }
        </div>
      </div>
      { headerFilters }
    </>
  }, [allCardsEndpoint, collectionName, filters.priceRange.min, forSaleCardsEndpoint, headerFilters, screen, profileUser?.salesLevel, profileUser?.salesRating, profileUserId, t, unitId])

  useEffect(() => {
    if (screen !== 'cards' || unitId || !profileUser || !profileUserId || profileUserId === 'me') return

    const ownerId = Number(profileUserId)
    if (!Number.isFinite(ownerId) || ownerId <= 0) return

    applyDocumentMeta(collectionPageMeta({
      owner: profileUser?.displayName,
      id: ownerId,
      salesRating: profileUser?.salesRating,
      salesLevel: profileUser?.salesLevel,
    }))
  }, [profileUser, profileUserId, screen, unitId])

  useEffect(() => {
    if (unitId) {
      setSearchboxPlaceholder?.()
      setIsUserProfile?.(false)

      return () => {
        setSearchboxPlaceholder?.()
        setIsUserProfile?.(false)
      }
    }

    if (!profileUser) return

    const displayName = profileUser.displayName ?? t('cards.item.default_display_name') ?? ''
    setSearchboxPlaceholder?.(t('profile.search_placeholder', { name: displayName }))
    setIsUserProfile?.(true)

    return () => {
      setSearchboxPlaceholder?.()
      setIsUserProfile?.(false)
    }
  }, [setIsUserProfile, setSearchboxPlaceholder, t, profileUser, unitId])

  if (profileUserLoading && !showingUnit) {
    return <>{ t('app.loading') }</>
  }

  if (!profileUserId && !showingUnit) {
    return <GenericNotFound />
  }

  return (
    <>
      { header }
      { currentView }
    </>
  )
}

export { UserProfile }
